// Guide §12.2 — Bible 5.2, 6.3, 10.0. All prices live here and nowhere else (Guide §0.1).
import type { PaidPlan, Plan, SmsPackageKey } from './enums';
import { Decimal, money } from './money';
import { MS_DAY } from './time';

export const PERIOD_DAYS = 30;
export const GRACE_DAYS = 3; // past_due grace (Bible DEC-08)
export const TRIAL_DAYS = 15; // Bible 6.2
export const TRIAL_FREE_POINTS = 50;
export const TRIAL_EXTENSION_MAX_DAYS = 7; // Bible 17.4

/** ৳ per 30-day period. Enterprise is by contract. */
export const PLAN_PRICE = { free: 0, basic: 149, pro: 299, business: 499 } as const;
/**
 * ৳/shop/month. Founder decision (final, Phase 6): only Pro and Business may buy extra shops; Free and Basic cannot (Bible 10.0: multi-shop locked).
 * An extra shop has no plan of its own — it runs under the account's current plan, so it can never unlock more than that plan gives
 * (entitlements are per account).
 */
export const EXTRA_SHOP_PRICE = 199;
/** Plans that may buy extra shops (Free, Basic and Trial cannot; Enterprise has no limit). */
export const EXTRA_SHOP_PLANS = ['pro', 'business'] as const;
export const EXTRA_SHOP_MAX = 20;
export const canBuyExtraShops = (plan: Plan): boolean => (EXTRA_SHOP_PLANS as readonly string[]).includes(plan);

/** Personal Savings (Bible 6.8.1, DL-22): a separate paid product, never free. Price & Business bonus are Founder-confirmed values. */
export const PERSONAL_PRICE = { monthly: 99, yearly: 999 } as const; // Founder, v10.1 (was 49 / 499); yearly ≈ 2 months free
export const PERSONAL_PERIOD_DAYS = { monthly: 30, yearly: 365 } as const;
export const PERSONAL_TRIAL_DAYS = 15; // once per account, separate from the Business trial
export const PERSONAL_GRACE_DAYS = 3;
/** Business (and Enterprise) accounts get Personal as a bonus when SiteConfig personal.config.businessBonus is on (proposal in 6.8.1). */
export const PERSONAL_BONUS_PLANS = ['business', 'enterprise'] as const;

export interface SmsPackage {
  price: number;
  points: number;
  /** Bonus vs base (2 points per ৳) for display */
  bonusPct: number;
}
export const SMS_PACKAGES: Record<SmsPackageKey, SmsPackage> = {
  starter: { price: 50, points: 100, bonusPct: 0 },
  standard: { price: 200, points: 450, bonusPct: 12.5 },
  business: { price: 500, points: 1200, bonusPct: 20 },
};

/** Monthly free SMS points granted at the start of each paid period (Bible 5.3). Enterprise: by contract. */
export const FREE_SMS_POINTS: Record<Plan, number> = { free: 0, basic: 20, pro: 50, business: 150, enterprise: 0 };
export const LOW_SMS_POINTS_THRESHOLD = 20; // Bible 5.1 "কম ব্যালেন্স"
export const ADMIN_SMS_GRANT_MAX = 500; // Bible 17.4
export const COMP_PLAN_MAX_DAYS = 90;
export const COMP_PLAN_APPROVAL_DAYS = 30;
export const REFUND_SECOND_APPROVER_ABOVE = 5000; // Bible 6.5 / 17.2

export const remainingDays = (end: Date, now: Date): number => Math.max(0, Math.ceil((+end - +now) / MS_DAY));

/** Upgrade: (new − old) × remaining ÷ 30, rounded up to ৳1, min ৳10 (Bible 6.3). */
export const upgradeCharge = (from: PaidPlan | 'free', to: PaidPlan, end: Date, now: Date): number =>
  Math.max(10, Math.ceil(((PLAN_PRICE[to] - PLAN_PRICE[from]) * remainingDays(end, now)) / PERIOD_DAYS));

/** Adding an extra shop mid-period (any paid plan): prorated, min ৳10. */
export const extraShopCharge = (end: Date, now: Date, count = 1): number =>
  Math.max(10, Math.ceil((EXTRA_SHOP_PRICE * count * remainingDays(end, now)) / PERIOD_DAYS));

export const renewalAmount = (plan: PaidPlan, extraShops: number): number =>
  PLAN_PRICE[plan] + Math.max(0, extraShops) * EXTRA_SHOP_PRICE;

// ── Plan terms (Phase 7): pay for 1, 3, 6, 12, 24 months — or any number up to the maximum — and get a discount for committing longer ──
export interface TermTier { months: number; pct: number }
export interface TermsConfig {
  enabled: boolean;
  /** longest term a customer may type in as "custom" */
  maxMonths: number;
  /** quick-pick buttons */
  presets: number[];
  /** the discount applies from `months` upward (a 9-month custom term gets the 6-month discount) */
  tiers: TermTier[];
}
export const DEFAULT_TERMS: TermsConfig = {
  enabled: true,
  maxMonths: 36,
  presets: [1, 3, 6, 12, 24],
  tiers: [{ months: 1, pct: 0 }, { months: 3, pct: 5 }, { months: 6, pct: 10 }, { months: 12, pct: 17 }, { months: 24, pct: 25 }, { months: 36, pct: 30 }],
};
/** A one-month term is the usual 30 days; longer terms are N/12 of a 365-day year, so 12 months = 365 days and 24 = 730. */
export const termDays = (months: number): number => (months <= 1 ? PERIOD_DAYS : Math.round((months * 365) / 12));
export const termDiscountPct = (months: number, tiers: TermTier[] = DEFAULT_TERMS.tiers): number =>
  tiers.reduce((best, t) => (t.months <= months && t.months >= best.months ? t : best), { months: 0, pct: 0 }).pct;
export interface TermPrice { months: number; list: number; discountPct: number; total: number; perMonth: number; saved: number }
/** `monthly` = the plan (plus extra shops) for one month. Whole taka, rounded to the nearest ৳1. */
export function termPrice(monthly: number, months: number, tiers: TermTier[] = DEFAULT_TERMS.tiers): TermPrice {
  const m = Math.max(1, Math.floor(months));
  const list = monthly * m;
  const discountPct = termDiscountPct(m, tiers);
  const total = Math.round((list * (100 - discountPct)) / 100);
  return { months: m, list, discountPct, total, perMonth: Math.round(total / m), saved: list - total };
}

export type VatMode = 'inclusive' | 'exclusive';
export interface VatConfig {
  mode: VatMode;
  /** 0.15 = 15% */
  rate: number;
}
/** Placeholder until E1 (Accounts & Legal) confirms: prices shown VAT-inclusive, 15% worst case (Bible 5.2). */
export const DEFAULT_VAT: VatConfig = { mode: 'inclusive', rate: 0.15 };

export interface ChargeBreakdown {
  /** Base price as listed (what the plan costs) */
  base: Decimal;
  vatAmount: Decimal;
  /** What the customer is actually charged */
  total: Decimal;
  mode: VatMode;
  rate: number;
}

/** Guide §12.2: Inclusive: VAT = price × r ÷ (1 + r). Exclusive: charge = price × (1 + r). */
export function applyVat(price: number | Decimal, cfg: VatConfig = DEFAULT_VAT): ChargeBreakdown {
  const base = money(price);
  const r = new Decimal(cfg.rate);
  if (cfg.mode === 'inclusive') {
    const vat = money(base.times(r).dividedBy(r.plus(1)));
    return { base, vatAmount: vat, total: base, mode: cfg.mode, rate: cfg.rate };
  }
  const vat = money(base.times(r));
  return { base, vatAmount: vat, total: base.plus(vat), mode: cfg.mode, rate: cfg.rate };
}

/** Bible 5.2: per-point net revenue after VAT and payment fee must stay ≥ gateway cost + 15%. */
export function smsPointNetRevenue(pkg: SmsPackage, vatRate = 0.15, paymentFee = 0.02): number {
  return (pkg.price / (1 + vatRate)) * (1 - paymentFee) / pkg.points;
}
export const SMS_GATEWAY_COST_PER_SEGMENT = 0.3;

/** Receipt number DD-YYYY-NNNNNN (Guide §12.4) */
export const formatReceiptNo = (year: number, seq: number): string => `DD-${year}-${String(seq).padStart(6, '0')}`;

export const PLAN_LABELS: Record<Plan, { bn: string; en: string }> = {
  free: { bn: 'ফ্রি', en: 'Free' },
  basic: { bn: 'বেসিক', en: 'Basic' },
  pro: { bn: 'প্রো', en: 'Pro' },
  business: { bn: 'বিজনেস', en: 'Business' },
  enterprise: { bn: 'এন্টারপ্রাইজ', en: 'Enterprise' },
};
